import React from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '../../layouts/DashboardShell';
import Button from '../../shared/components/ui/Button';
import { clientSideLinks } from './clientRoutes';
import JobCard from '../worker/components/JobCard';
import FilterChips from '../worker/components/FilterChips';
import useMyFeed from '../../features/jobs/hooks/useMyFeed';
import useDashboardData from '../../features/jobs/hooks/useDashboardData';
import CardIcon from '../../shared/components/icons/CardIcon';

function Tile({ title, subtitle, right, icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-base font-semibold text-navy"><CardIcon name={icon} />{title}</p>
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        </div>
        {right}
      </div>
    </div>
  );
}

function ClientDashboardPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = React.useState('town');
  const { jobs, loading, error } = useMyFeed(activeFilter);
  const { dashboard, loading: dashboardLoading } = useDashboardData();
  const activeJob = dashboard?.activeJobs?.[0];
  const recentApplication = dashboard?.recentApplications?.[0];
  const activeJobSubtitle = activeJob
    ? `${activeJob.skill?.name || 'General work'} • ${activeJob.applicantsCount || 0} applicants • Status: ${activeJob.status || 'Open'}`
    : 'No active jobs yet';
  const recentApplicationSubtitle = recentApplication
    ? `${recentApplication.applicant?.name || 'Applicant'} applied • ⭐ ${recentApplication.applicant?.rating ?? 'New'} rating • ${recentApplication.applicant?.completedJobs || 0} completed jobs`
    : 'No recent applications';

  return (
    <DashboardShell
      role="CLIENT"
      sideLinks={clientSideLinks}
      rightPanel={
        <div className="space-y-4">
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold text-navy">Spending summary</p>
            <p className="mt-2 text-sm text-slate-600">This month: <span className="font-semibold text-navy">-</span></p>
            <p className="mt-1 text-sm text-slate-600">Completed jobs: <span className="font-semibold text-navy">-</span></p>
          </div>
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold text-navy">Tips</p>
            <p className="mt-2 text-sm text-slate-600">Add clear photos and location to attract top workers.</p>
          </div>
        </div>
      }
    >
      <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-500"><CardIcon name="home" />Welcome back</p>
        <p className="mt-2 text-sm text-slate-600">Post jobs fast, manage workers, and track progress.</p>
        <div className="mt-5">
          <Button variant="primary" onClick={() => navigate('/client/post-job')}>
            + Post a Job
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        <Tile
          title="Active jobs"
          icon="briefcase"
          subtitle={dashboardLoading ? 'Loading active jobs...' : activeJobSubtitle}
          right={<button type="button" onClick={() => navigate('/client/jobs')} className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{activeJob?.status || 'View'}</button>}
        />
        <Tile
          title="Recent application"
          icon="application"
          subtitle={dashboardLoading ? 'Loading recent applications...' : recentApplicationSubtitle}
          right={<button type="button" onClick={() => navigate('/client/applications')} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">View</button>}
        />
      </div>

      <FilterChips
        filters={[
          { label: 'Nearby jobs', value: 'town' },
          { label: 'Urgent', value: 'urgent' },
          { label: 'High pay', value: 'highpay' },
        ]}
        active={activeFilter}
        onChange={setActiveFilter}
      />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-navy"><CardIcon name="briefcase" />Jobs feed</h2>
          <button type="button" onClick={() => navigate('/client/all-jobs')} className="text-sm font-semibold text-navy hover:underline">View all</button>
        </div>
        {loading && <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading jobs...</div>}
        {error && <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">Error: {error}</div>}
        {!loading && !error && jobs.length === 0 && <div className="rounded-3xl bg-white p-8 text-center text-slate-500 shadow-sm">No jobs found for this filter.</div>}
        {!loading && !error && jobs.length > 0 && <div className="grid gap-4">{jobs.map((job) => <JobCard key={job.id} job={job} linkTo={`/client/job/${job.id}`} showActions={false} />)}</div>}
      </section>
    </DashboardShell>
  );
}

export default ClientDashboardPage;

